/*
 * @FileName       : reports.saga.js
 * @Version        : 1.0
 * @DateOfCreation : 04/21/2020
 * @Author         : $u^^!t @ FFF Enterprises
 * @Dependencies   : N/A
 * @Description    : User saga to store all the reports events.
 *
 * Copyright (c) 2020, FFF Enterprises. All rights reserved.
 */

import { call, put, delay, select } from 'redux-saga/effects';
import { ReportActions, UIAction, UserActions } from '../actions/index';
import { AuditService, ReportService } from '../services';
import { InventoryDetail } from '../models/reports/Inventory';
import { Constants } from '../../constants/Constants';

/*
 * inventory history call
 */
export function* getInventoryReport(action: ReturnType<typeof ReportActions.getInventoryHistory>) {
	try {
		yield put(UIAction.showLoader(true));
		if (!action.payload.export) {
			yield put(ReportActions.successInventoryHistory(null));
		}
		const { data } = yield call(ReportService.getInstance().inventoryHistory, action.payload);
		if (!action.payload.export && (!data.result || data.result.length === 0)) {
			yield put(
				UserActions.setSnackbarMessage({
					message: Constants.ALERT.NO_RECORD_FOUND,
					type: 'info',
				}),
			);
		}
		if (action.payload.export) {
			var blob = new Blob([data]);
			var link = document.createElement('a');
			link.href = window.URL.createObjectURL(blob);
			link.download = 'inventory_history_report.csv';
			link.click();
			yield put(UIAction.showLoader(false));
			return;
		}
		yield put(ReportActions.successInventoryHistory(data)); //result array and total_record number
		yield put(UIAction.showLoader(false));
	} catch (err) {
		yield put(UIAction.showLoader(false));
		console.log('report saga error', err);
	}
}

export function* getInventoryGroup(action: ReturnType<typeof ReportActions.getInventoryGroup>) {
	try {
		yield put(UIAction.showLoader(true));
		yield put(UIAction.setApiError(null));
		const { data } = yield call(ReportService.getInstance().inventoryGroupByTenants, action.payload);
		yield put(ReportActions.successInventoryGroup(data));
		yield put(UIAction.showLoader(false));

		if (!data || !data.result || data.result.length === 0) {
			yield put(
				UserActions.setSnackbarMessage({
					message: Constants.ALERT.NO_RECORD_FOUND,
					type: 'info',
				}),
			);
		}
	} catch (err: any) {
		yield put(UIAction.showLoader(false));
		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;

		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in getInventoryGroup`,
				type: 'error',
			}),
		);

		yield put(UIAction.showLoader(false));
	}
}

export function* getRecentlyVended(action: ReturnType<typeof ReportActions.getRecentlyVended>) {
	try {
		yield put(UIAction.showLoader(true));
		yield put(UIAction.setApiError(null));
		const { data } = yield call(ReportService.getInstance().getRecentlyVended, action.payload);
		if (action.payload.groupBy) {
			yield put(ReportActions.setTransactionGroupNames(data.groupNames));
			yield put(ReportActions.setTransactionGroupLog(data.groups));
		}
		if (!action.payload.groupBy) {
			yield put(ReportActions.successRecentlyVended(data));
			if (!data || !data.result || data.result.length === 0) {
				yield put(
					UserActions.setSnackbarMessage({
						message: Constants.ALERT.NO_RECORD_FOUND,
						type: 'info',
					}),
				);
			}
		}
		yield put(UIAction.showLoader(false));
	} catch (err: any) {
		console.log('Transaction log error:', err);
		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;

		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in getRecentlyVended`,
				type: 'error',
			}),
		);

		yield put(UIAction.showLoader(false));
	}
}

export function* getTransactionLogGroup(action: ReturnType<typeof ReportActions.getTransactionGroupLog>): any {
	try {
		yield put(UIAction.showLoader(true));
		yield put(UIAction.setApiError(null));
		const { data } = yield call(ReportService.getInstance().getRecentlyVended, action.payload.filter);
		const groupData = yield select(state => state.reports.transactionGroups);
		const newGroupData = Object.assign({}, groupData);
		newGroupData[action.payload.groupKey] = data;
		yield put(ReportActions.setTransactionGroupLog(newGroupData));
		yield put(UIAction.showLoader(false));
	} catch (err: any) {
		console.log('Transaction log grouping error:', err);
		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;

		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in getTransactionLogGroup`,
				type: 'error',
			}),
		);

		yield put(UIAction.showLoader(false));
	}
}

export function* getTransactionLogCsvExport(action: ReturnType<typeof ReportActions.transactionLogExport>) {
	try {
		yield put(UIAction.showLoader(true));
		yield put(UIAction.setApiError(null));
		yield call(ReportService.getInstance().requestTransactionLogCsvExport, action.payload);
		yield put(UIAction.showLoader(false));
	} catch (err: any) {
		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;

		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in getTransactionLogCsvExport`,
				type: 'error',
			}),
		);

		yield put(UIAction.showLoader(false));
	}
}

export function* fixTableColumn(action: ReturnType<typeof ReportActions.fixTableColumn>) {
	try {
		console.log('action', action);
		yield put(ReportActions.fixTableColumnSuccess(action.payload));
	} catch (err) {
		console.log('saga error' + err);
	}
}

export function* setFilter(action: ReturnType<typeof ReportActions.setFilter>) {
	try {
		yield put(ReportActions.setFilterSuccess(action.payload));
	} catch (err) {
		console.log('saga error' + err);
	}
}

export function* getDispensedProducts(action: ReturnType<typeof ReportActions.getDispensedProducts>) {
	try {
		yield put(UIAction.showLoader(true));
		yield put(UIAction.setApiError(null));
		const { data } = yield call(ReportService.getInstance().getDispensedProducts, action.payload);
		yield put(UIAction.showLoader(false));
		if (action.payload.export) {
			var blob = new Blob([data]);
			var link = document.createElement('a');
			link.href = window.URL.createObjectURL(blob);
			link.download = `${action.payload.customerId}_dispensed_products_report_${action.payload.startDate}_${action.payload.endDate}.csv`;
			link.click();
			return;
		}
		yield put(ReportActions.setDispensedProducts(data));
		if (!data || !data.results || data.results.length === 0) {
			yield put(
				UserActions.setSnackbarMessage({
					message: Constants.ALERT.NO_RECORD_FOUND,
					type: 'info',
				}),
			);
		}
	} catch (err: any) {
		console.log('dispatched products report saga error', err);

		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;

		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in getDispensedProducts`,
				type: 'error',
			}),
		);

		yield put(UIAction.showLoader(false));
	}
}

export function* getCycleCounts(action: ReturnType<typeof ReportActions.getCycleCounts>) {
	try {
		yield put(UIAction.showLoader(true));
		yield put(UIAction.setApiError(null));
		const { data } = yield call(AuditService.getInstance().getAuditList, action.payload.filter);
		yield put(UIAction.showLoader(false));
		if (action.payload.doExport) {
			action.payload.doExport();
			return;
		}
		yield put(ReportActions.setCycleCounts(data));
		if (!data || !data.data || data.total_count === 0) {
			yield put(
				UserActions.setSnackbarMessage({
					message: Constants.ALERT.NO_RECORD_FOUND,
					type: 'info',
				}),
			);
		}
	} catch (err: any) {
		console.log('Cycle Counts report saga error', err);

		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;

		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in getCycleCounts`,
				type: 'error',
			}),
		);

		yield put(UIAction.showLoader(false));
	}
}

export function* getWarehouseShipments(action: ReturnType<typeof ReportActions.getWarehouseShipments>) {
	try {
		yield put(UIAction.showLoader(true));
		yield put(UIAction.setApiError(null));
		const { data } = yield call(ReportService.getInstance().getWarehouseShipments, action.payload);
		yield put(UIAction.showLoader(false));
		if (action.payload.export) {
			var blob = new Blob([data]);
			var link = document.createElement('a');
			link.href = window.URL.createObjectURL(blob);
			link.download = `${action.payload.customerId}_warehouse_shipments_report_${action.payload.startDate}_${action.payload.endDate}.csv`;
			link.click();
			return;
		}
		yield put(ReportActions.setWarehouseShipments(data));
		if (!data || !data.results || data.results.length === 0) {
			yield put(
				UserActions.setSnackbarMessage({
					message: Constants.ALERT.NO_RECORD_FOUND,
					type: 'info',
				}),
			);
		}
	} catch (err: any) {
		console.log('warehouse shipment report saga error', err);

		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;

		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in getWarehouseShipments`,
				type: 'error',
			}),
		);

		yield put(UIAction.showLoader(false));
	}
}

export function* getRestockOrderDetails_igShipmentItems(
	action: ReturnType<typeof ReportActions.getRestockOrderDetails_igShipmentItems>,
) {
	try {
		const { data } = yield call(ReportService.getInstance().getRestockOrderDetails_igShipmentItems, action.payload);
		yield put(ReportActions.setRestockOrderDetails_igShipmentItems(data));
	} catch (err) {
		console.log('getRestockOrderDetails_igShipmentItems saga error', err);
	}
}

export function* getRestockOrderDetails_restockRequest(
	action: ReturnType<typeof ReportActions.getRestockOrderDetails_restockRequest>,
) {
	try {
		const { data } = yield call(ReportService.getInstance().getRestockOrderDetails_restockRequest, action.payload);
		yield put(ReportActions.setRestockOrderDetails_restockRequest(data));
	} catch (err) {
		console.log('getRestockOrderDetails_restockRequest saga error', err);
	}
}

export function* getRestockOrderDetails_warehouseShipment(
	action: ReturnType<typeof ReportActions.getRestockOrderDetails_warehouseShipment>,
) {
	try {
		const { data } = yield call(ReportService.getInstance().getRestockOrderDetails_warehouseShipment, action.payload);
		yield put(ReportActions.setRestockOrderDetails_warehouseShipment(data));
	} catch (err) {
		console.log('getRestockOrderDetails_warehouseShipment saga error', err);
	}
}

export function* getRestockOrderDetails_transactions(
	action: ReturnType<typeof ReportActions.getRestockOrderDetails_transactions>,
) {
	try {
		const { data } = yield call(ReportService.getInstance().getRestockOrderDetails_transactions, action.payload);
		yield put(ReportActions.setRestockOrderDetails_transactions(data));
	} catch (err) {
		console.log('getRestockOrderDetails_transactions saga error', err);
	}
}

export function* getRestockOrderDetails_sapOrders(
	action: ReturnType<typeof ReportActions.getRestockOrderDetails_sapOrders>,
) {
	try {
		const { data } = yield call(ReportService.getInstance().getRestockOrderDetails_sapOrders, action.payload);
		yield put(ReportActions.setRestockOrderDetails_sapOrders(data));
	} catch (err) {
		console.log('getRestockOrderDetails_sapOrders saga error', err);
	}
}
